import React, { useCallback, useContext, useEffect, useState } from 'react';
import './Chat.styles.scss';
import ChatButton from '../ChatButton/ChatButton';
import ChatWindow from '../ChatWindow/ChatWindow';
import { CAContext } from '../../contexts/CAContext';
import { useCustomTracking } from '../../hooks/custom-hooks/useTracking';
import { useUtilityHooks } from '../../hooks/useUtilityHooks';
import { useMIAWHooks } from '../../hooks/useMIAWHooks';
import { useChatbotHooks } from '../../hooks/useChatbotHook';
import { getChatbotToken, getMIAWEnv, setChatWindowState, setUnreadCount } from '../../utils/session-storage-utils';
import { CHAT_BUTTON_TEXT, CHAT_HEADER_TEXT } from '../../config/app-config';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { useCustomChatEvents } from '../../hooks/custom-hooks/useCustomChatEvents';
import { CaseDataContext } from '../../contexts/CaseDataContext';
import { useNRLogging } from '../../hooks/custom-hooks/useNRLogging';

interface IChatProps {
  onChatButtonClick?: () => void;
}

const Chat: React.FC<IChatProps> = ({ onChatButtonClick }) => {
  const { logCustomAttributesNR, logChat } = useNRLogging();
  const { sendChatEvents, handleAddEventListeners, addOpenChatEventListener } = useCustomChatEvents();
  const {
    state: { caseData },
  } = useContext(CaseDataContext);
  const {
    chatState: { chatWindow, chatSession, unreadMessages, enableChatbot, gcpChatData, isChatEventListenersAdded, enableBHCheckInEmode },
    setChatState,
  } = useContext(CAContext);

  const [showChat, setShowChat] = useState(false);

  const {
    state: { agentName },
  } = useContext(MiawChatContext);
  const { sendTrackingEvent } = useCustomTracking();
  const [pageURL, setPageURL] = useState<string>('');

  const {
    utilityHooksLoading: { gcpChatLoading, userInfoLoading },
  } = useUtilityHooks();

  const {
    miawHooksData: { miawChatRetrieveData, miawChatStartData },
    miawHooksTriggers: { miawChatRetrieveTrigger },
  } = useMIAWHooks();

  const {
    chatbotHooksData: { chatbotRetrieveData, chatbotStartData },
    chatbotHooksTriggers: { triggerChatbotRetrieve },
  } = useChatbotHooks();

  // Handle chat button click
  const onButtonClick = useCallback(() => {
    if (enableChatbot || chatSession === 'chatbotSession') {
      sendTrackingEvent('Chatbot Clicked', 'Chatbot:CTA', 'chatCtaClicked');
      logCustomAttributesNR('ca-chatbot-info', {
        logName: 'chatbot_clicked',
        logLevel: 'info',
        message: 'Chatbot clicked',
      });
    } else {
      sendTrackingEvent('Chat Initiated', 'Chat help initiated - Chat Anywhere', `${caseData?.pagePath};Chat Initiate`, 'Global Chat');
      logChat('CA_HELP_BUTTON_CLICKED', {});
    }

    // Open Chat window on Click
    setChatState((prevState) => ({
      ...prevState,
      chatWindow: 'maximized',
    }));
    // Trigger External onClick handler
    if (onChatButtonClick) {
      onChatButtonClick();
    }
  }, [onChatButtonClick, enableChatbot, chatSession, caseData]);

  useEffect(() => {
    console.log('GCP Chat Data:', gcpChatData);
    if (gcpChatData) {
      handleAddEventListeners();
      addOpenChatEventListener(onButtonClick);
    }
  }, [gcpChatData, onButtonClick]);

  useEffect(() => {
    if (isChatEventListenersAdded) {
      sendChatEvents('onChatReady', {});
      if (chatSession === 'miawChatSession' || chatSession === 'chatbotSession') {
        sendChatEvents('onSessionContinuation', {});
      }
    }
  }, [isChatEventListenersAdded]);

  /** Fetch messages for both miaw and chatbot on page refresh */
  useEffect(() => {
    if (chatSession === 'miawChatSession') {
      const chatbotToken = getChatbotToken();
      /** If data not retireved and new session is not created */
      if (!miawChatRetrieveData && !miawChatStartData) {
        miawChatRetrieveTrigger();
      }
      // Only trigger chatbot retrieve if token is present
      if (!chatbotRetrieveData && !chatbotStartData && chatbotToken) {
        triggerChatbotRetrieve();
      }
    }
    if (chatSession === 'chatbotSession' && !chatbotRetrieveData && !chatbotStartData) {
      triggerChatbotRetrieve();
    }
  }, [chatSession, miawChatRetrieveData, chatbotRetrieveData, miawChatStartData, triggerChatbotRetrieve, triggerChatbotRetrieve]);

  // Reset unread messages when window is maximized
  useEffect(() => {
    if (unreadMessages && chatWindow === 'maximized') {
      setChatState((prevState) => ({
        ...prevState,
        unreadMessages: 0,
      }));
    }
  }, [chatWindow, setChatState, unreadMessages]);

  /** To persist value across pages, set unread count in local storage */
  useEffect(() => {
    setUnreadCount(unreadMessages || 0);
  }, [unreadMessages]);

  // Track chat view
  const [isChatEventSent, setIsChatEventSent] = React.useState(false);

  useEffect(() => {
    if (!isChatEventSent) {
      if (enableChatbot) sendTrackingEvent('Chatbot Viewed', 'Chatbot:CTA', 'chatCtaViewed');
      setIsChatEventSent(true);
    }
  }, [isChatEventSent, sendTrackingEvent]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPageURL(window.location.href);
    }
  }, []);

  useEffect(() => {
    if (pageURL)
      logCustomAttributesNR('ca-chatbot-info', {
        logName: 'chat_page_change',
        logLevel: 'info',
        message: `Chat page changed to: ${pageURL}, session: ${chatSession}`,
        chatSession,
      });
  }, [pageURL]);

  useEffect(() => {
    setChatWindowState(chatWindow);
    console.log('Chat window state set to:', chatWindow, isChatEventListenersAdded);
    if (chatWindow === 'maximized' && isChatEventListenersAdded) {
      sendChatEvents('onMaximize', {});
    }
  }, [chatWindow, isChatEventListenersAdded]);

  useEffect(() => {
    let btnTxt = '';
    let headerTxt = '';
    switch (chatSession) {
      case 'chatbotSession':
        btnTxt = CHAT_BUTTON_TEXT.CHATBOT;
        headerTxt = CHAT_HEADER_TEXT.CHATBOT;
        break;
      case 'miawChatSession':
        btnTxt = agentName && agentName !== '' ? agentName : CHAT_BUTTON_TEXT.MIAW_CHAT;
        headerTxt = agentName && agentName !== '' ? agentName : CHAT_HEADER_TEXT.MIAW_CHAT;
        break;
      default:
        btnTxt = enableChatbot ? CHAT_BUTTON_TEXT.CHATBOT : CHAT_BUTTON_TEXT.MIAW_CHAT;
        headerTxt = enableChatbot ? CHAT_HEADER_TEXT.CHATBOT : CHAT_HEADER_TEXT.MIAW_CHAT;
        break;
    }
    setChatState((prevState) => ({
      ...prevState,
      chatButtonText: btnTxt,
      chatHeaderText: headerTxt,
    }));
  }, [chatSession, enableChatbot, agentName]);

  useEffect(() => {
    if (pageURL?.includes('localhost') || pageURL?.includes('staging') || getMIAWEnv() === 'sandbox') {
      /** For testing environments, always show chat */
      setShowChat(true);
    } else if (chatSession === 'chatbotSession' || chatSession === 'miawChatSession') {
      /** If chat is ongoing, show chat */
      setShowChat(true);
    } else if (gcpChatData && gcpChatData.status.isChatEnabled) {
      /** if chatEnabled flag is true, */
      setShowChat(true);
    } else if (enableBHCheckInEmode && enableChatbot && gcpChatData && gcpChatData.status.isEmodeEnabled) {
      /** For chatbot pages, check if emode is engaged  */
      setShowChat(true);
    } else if (!enableBHCheckInEmode && enableChatbot) {
      /** For chatbot pages, show chat if chatbot is enabled */
      setShowChat(true);
    } else {
      setShowChat(false);
    }
  }, [gcpChatData, pageURL, chatSession, enableChatbot, enableBHCheckInEmode]);

  if (gcpChatLoading || userInfoLoading) {
    return null;
  }

  if (showChat) {
    return (
      <>
        <ChatButton onClick={onButtonClick} />
        <ChatWindow />
      </>
    );
  }

  return null;
};

export default Chat;
